import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress,
  InputAdornment, LinearProgress, MenuItem, Paper, Select, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

import { api } from '../api/client';
import { useShowPeople, useWhom } from '../features/people/whom';
import { PlaceCoverageDetail } from './PlaceCoverageDetail';
import type { City, PoiCoverage, RoutePlan } from '../api/types';
import { readableHours } from './DurationField';
import { STOP_COLOURS } from '../theme';

/*
  Nothing is filtered any more.

  Two filters used to narrow every block — to one person, or to one saved
  plan — and both were set from the by-person block, which has moved into
  the window a place opens (see PlaceCoverageDetail). With nothing able to set
  them, a strip saying "counting only..." could only ever say "everything", so
  the model is asked the plain question, from a constant that cannot change
  between renders.
*/
const EVERYTHING: CoverageFilters = { creator: null, recordId: null };

/**
 * How near a pin has to be to a saved stop to count as the same spot.
 *
 * Stated twice, here and in the server's query, because the number is part
 * of what this screen claims and the sentence explaining it has to carry the
 * figure. See `sameSpotMetres` in internal/savedroute/coverage.go.
 */
const SAME_SPOT_METRES = 40;
import {
  arrangePlaces, buildCoverage, coveringDate, trackedPlaces,
} from '../util/coverage';
import type {
  CoverageFilters, PlaceCoverage, PlaceShow, PlaceSort,
} from '../util/coverage';

/*
 * What this round goes over again, and whose work covered it.
 *
 * Read out of the work people have saved — the saved routes and the saved
 * operations — because that is the only record this system keeps. Nobody
 * confirms a visit, so everything here is a plan somebody committed to
 * rather than a fact about the ground, and every duration is time a plan
 * ALLOWED rather than time anybody spent. That is said where a number could
 * be read the other way, and not in a paragraph nobody reads.
 *
 * The arithmetic lives in util/coverage.ts, which has no React in it and is
 * where the three words this panel counts in — place, occurrence, record —
 * are defined.
 */

/** "8 Oct 2026", or '' where the record has no date. */
function onDay(at: string | null): string {
  if (!at) return '';

  return new Date(at).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** "9 Oct", from a plain YYYY-MM-DD or a YYYY-MM-DDTHH:MM. */
function planned(date: string): string {
  if (!date) return '';

  return new Date(`${date.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short',
  });
}

/** "9 Oct 2026", where the year is worth saying. */
function plannedFull(date: string): string {
  if (!date) return '';

  return new Date(`${date.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** "9:03 AM", from a YYYY-MM-DDTHH:MM. '' when the plan sets no hour. */
function clock(at: string): string {
  const time = at.slice(11, 16);
  if (!time || time === '00:00') return '';

  return new Date(`${at.slice(0, 10)}T${time}:00`).toLocaleTimeString(undefined, {
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * "8–11 Oct 2026", or one date where the work is all of a day.
 *
 * The year once, at the end, and the month only where the two differ —
 * which is how somebody would say it aloud.
 */
function span(from: string, to: string): string {
  if (!from) return '';
  if (from.slice(0, 10) === to.slice(0, 10)) return plannedFull(from);

  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  if (sameMonth) {
    return `${new Date(`${from.slice(0, 10)}T00:00:00`).getDate()}\u2013${plannedFull(to)}`;
  }

  return `${sameYear ? planned(from) : plannedFull(from)} \u2013 ${plannedFull(to)}`;
}

function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** A label with the rule behind it one hover away. */
function Explain({ text, children }: { text: string; children: ReactElement }) {
  return <Tooltip title={text} enterDelay={400} arrow>{children}</Tooltip>;
}

function Figure({ label, value, hint, muted = false }: {
  label: string;
  value: string;
  /** The rule behind the number: what is counted, and what is not. */
  hint: string;
  muted?: boolean;
}) {
  return (
    <Explain text={hint}>
      <Box sx={{ cursor: 'help' }}>
        <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
        </Stack>
        <Typography
          variant="body1"
          sx={{ fontWeight: 620, color: muted ? 'text.secondary' : 'text.primary' }}
        >
          {value}
        </Typography>
      </Box>
    </Explain>
  );
}

export function CoveragePanel({
  plan, city, basemapUrl, roundDate, ignoreSourceId, dayNote,
  onShowDetails, onStopFocus, onStopReveal, onStopHover,
}: {
  plan: RoutePlan;
  city: City;
  /**
   * The tile archive the page's own map draws, handed on to the window a
   * place opens into — see PlaceCoverageDetail. Without it that map has a
   * marker and no ground under it.
   */
  basemapUrl?: string | null;
  /**
   * The date this round is planned for, YYYY-MM-DD.
   *
   * What "before" is measured against, because the question is which work
   * came first on the ground, not which was typed in first. Without it
   * nothing can be later, and everything saved counts as earlier.
   */
  roundDate?: string;
  /**
   * A saved route or operation to leave out: the one being looked at.
   *
   * Without it, opening a saved round reports every one of its stops as
   * already covered — by itself — which is true and useless. The planner has
   * nothing to pass, because a round that has not been saved cannot be in
   * the record.
   */
  ignoreSourceId?: string;
  /**
   * Which day this is, on a screen that holds several.
   *
   * The panel analyses the round beside it, so on an operation that is one
   * day at a time. Saying which day stops the figures reading as the whole
   * operation's.
   */
  dayNote?: string;
  /** Opens the place panel for a stop, where the page has one. */
  onShowDetails?: (poiId: string) => void;
  /**
   * Takes the map to a stop AND opens what is known about it, as clicking a
   * name in the schedule does.
   */
  onStopFocus?: (index: number) => void;
  /**
   * Takes the map to a stop and does nothing else.
   *
   * What the number beside a place is for. Separate from `onStopFocus`
   * because on the planner that one also opens the place panel, and "show me
   * where this is" should not have to open a dialog to answer.
   */
  onStopReveal?: (index: number) => void;
  /** Highlights a stop on the map while the cursor is on its row. */
  onStopHover?: (index: number | null) => void;
}) {
  const showPeople = useShowPeople();
  const whom = useWhom();
  const places = useMemo(() => trackedPlaces(plan.stops), [plan]);

  const [coverage, setCoverage] = useState<PoiCoverage[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);


  /*
   * Whether work planned for later counts as coverage.
   *
   * Off by default: "covered before" is the question this tab exists to
   * answer, and a plan for next week has covered nothing. On, every saved
   * plan counts whatever its date — which answers the other question,
   * "is anybody else going here?".
   */
  const [countEverything, setCountEverything] = useState(false);

  /* How the place list is shown, which the filters do not touch. */
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<PlaceShow>('all');
  const [sort, setSort] = useState<PlaceSort>('route');
  /*
    The place whose window is open, by key.

    By key rather than by poi id because a dropped pin has no id and is
    still a place somebody can ask about.
  */
  const [openPlace, setOpenPlace] = useState<string | null>(null);

  /*
   * Asked once per set of places, not once per render.
   *
   * Keyed on the ids themselves rather than on the array holding them: that
   * array is rebuilt whenever the panel renders, and watching its identity
   * would re-ask the server on every hover of the itinerary beside it.
   */
  const asked = places
    .map((p) => (p.kind === 'place' ? p.poiId : `${p.key}@${p.lat},${p.lng}`))
    .join(',');

  useEffect(() => {
    let current = true;
    setCoverage(null);
    setFailed(false);
    if (places.length === 0) {
      setCoverage([]);

      return () => { current = false; };
    }

    /*
     * Two ways of asking, because there are two kinds of stop.
     *
     * A named place is looked up by its id. A pin has no id, so it is asked
     * about by where it is, and the server answers with whatever saved work
     * passes within a few paces of it — and how near.
     */
    void api.poiCoverage(
      city.id,
      places.filter((p) => p.kind === 'place').map((p) => p.poiId),
      places
        .filter((p) => p.kind === 'point')
        .map((p) => ({ key: p.key, lat: p.lat, lng: p.lng })),
    )
      .then((entries) => { if (current) setCoverage(entries); })
      .catch(() => { if (current) setFailed(true); });

    return () => { current = false; };
    // `asked` stands for the stops themselves; `places` is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city.id, asked, attempt]);

  useEffect(() => {
    setOpenPlace(null);
  }, [asked]);

  const model = useMemo(
    () => buildCoverage(places, coverage ?? [], {
      filters: EVERYTHING,
      ...(roundDate && !countEverything ? { roundDate } : {}),
      ...(roundDate && !countEverything && plan.startTime
        ? { roundTime: plan.startTime }
        : {}),
      ...(ignoreSourceId ? { ignoreRecordId: ignoreSourceId } : {}),
    }),
    [places, coverage, ignoreSourceId, roundDate, countEverything, plan.startTime],
  );

  /* The hour this round sets off, for the sentence that states the cut-off. */
  const roundClock = plan.startTime
    ? clock(`${roundDate || '2000-01-01'}T${plan.startTime}`)
    : '';

  const listed = useMemo(
    () => arrangePlaces(model.places, { query, show, sort }),
    [model.places, query, show, sort],
  );


  /*
   * What "covered" is measured against, in one sentence.
   *
   * Every figure in the first card rests on this, and it changes with the
   * filters and with whether this round has a date — so it is written once
   * and handed to each of them rather than restated three ways.
   */
  const baseline = [
    `Compared against every saved route and operation for ${city.name}`,
    roundDate
      ? `planned for a date before ${planned(roundDate)}`
      : 'whatever its date — this round has none set, so nothing can be said to come after it',
    `. A named place is matched by identity; a dropped pin by where it is, against anything saved within ${SAME_SPOT_METRES} m of it.`,
  ].join(' ').replace(' .', '.');

  if (failed) {
    return (
      <Alert
        severity="warning"
        variant="outlined"
        action={(
          <Button color="inherit" size="small" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        )}
      >
        The coverage record could not be read. The schedule beside this is unaffected.
      </Alert>
    );
  }

  if (coverage === null) {
    return (
      <Stack spacing={1.5} sx={{ p: 1 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Reading what has been covered before…
          </Typography>
        </Stack>
        <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
      </Stack>
    );
  }

  if (places.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        This round has no stops to report on yet.
      </Alert>
    );
  }

  const { totals } = model;

  /* The most-covered place of the round, for the line under the figures. */
  const busiest = model.places.reduce<PlaceCoverage | null>(
    (best, p) => (best === null || p.records > best.records ? p : best),
    null,
  );
  const nothingKnown = model.records.length === 0;

  return (
    <Stack spacing={2}>
      {dayNote && (
        <Typography variant="caption" color="text.secondary">
          {dayNote} — each day is analysed on its own tab.
        </Typography>
      )}

      {/* ── 1. the round as a whole ──────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        {/*
          Two numbers and the line that joins them.

          This card held five figures — places, minutes, plans, occurrences —
          and the reader had to work out which of them counted the same
          things. What it is actually asked is "how much of this is new?", and
          that is one sum. The minutes live beside the person who planned
          them, and the plan counts beside the place they cover, where each
          means something on its own.
        */}
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="h3">Previously Covered</Typography>
          <Explain text={baseline}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
          </Explain>
        </Stack>

        <Stack direction="row" spacing={4} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Figure
            label="Previously covered places"
            value={`${totals.covered} of ${totals.places}`}
            hint={baseline}
          />
          <Figure
            label="New places"
            value={String(totals.fresh)}
            hint={`Stops of this round that no saved plan in the comparison holds. New to the record — which is not the same as never visited, since nothing here records visits. ${baseline}`}
          />
        </Stack>

        {/*
          The bar is the round; the filled part is what has been covered
          before. It is the whole relationship between the two figures above,
          and it fits on one line.
        */}
        <Box
          sx={{
            display: 'flex',
            height: 6,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'action.hover',
            mt: 1.5,
          }}
        >
          {totals.covered > 0 && (
            <Box sx={{ flex: totals.covered, bgcolor: STOP_COLOURS.visit }} />
          )}
          {totals.fresh > 0 && (
            <Box sx={{ flex: totals.fresh, bgcolor: 'success.light' }} />
          )}
        </Box>

        {/*
          What the covered half is made of.

          "Six of nine" says how much of this round somebody has planned
          before; it does not say whether that was one person's afternoon or
          five people over two months, and those call for different
          decisions. These are the figures the by-person block used to carry
          before it moved into each place's own window — the round-level
          half of it, which belongs here.
        */}
        {totals.records > 0 && (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ flexWrap: 'wrap', mt: 1.25 }}
          >
            <Explain text={`Distinct saved plans that take in at least one place of this round: ${totals.routes} ${totals.routes === 1 ? 'route' : 'routes'} and ${totals.operations} ${totals.operations === 1 ? 'operation' : 'operations'}. An operation that stops somewhere on three of its days is one plan.`}>
              <Chip
                size="small"
                variant="outlined"
                icon={<PlaceOutlinedIcon />}
                label={countLabel(totals.records, 'saved plan', 'saved plans')}
                sx={{ cursor: 'help' }}
              />
            </Explain>

            {showPeople && model.creators.length > 0 && (
              <Explain text={`Who saved that work: ${model.creators.map((c) => whom(c.by, c.byName)).join(', ')}. Who SAVED a plan, not who went — nothing here records a visit.`}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<PersonOutlineIcon />}
                  label={countLabel(model.creators.length, 'person', 'people')}
                  sx={{ cursor: 'help' }}
                />
              </Explain>
            )}

          </Stack>
        )}

        {/* The place with the most behind it, which is where a reader looks
            first and otherwise has to find by sorting the list. */}
        {busiest && busiest.records > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {`Most covered: ${busiest.place.name} — in ${countLabel(busiest.records, 'saved plan', 'saved plans')}`}
            {busiest.lastAt ? `, last ${onDay(busiest.lastAt)}` : ''}
          </Typography>
        )}
        {/*
          Which days the answer is drawn from.

          "12 of 12 covered" means something different if the saved record
          starts last week than if it starts last year, and nothing else on
          the screen says which.
        */}
        {totals.earliest && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {`Based on saved plans from ${span(totals.earliest, totals.latest)}`}
            {roundDate && countEverything
              ? ' (every date, including after this round)'
              : roundDate
                ? ` (up to ${plannedFull(roundDate)}${roundClock ? `, ${roundClock}` : ''})`
                : ''}
          </Typography>
        )}

        {/*
          The cut-off, which somebody may want to lift.

          The question is usually "has anyone been here before me?", and for
          that a plan for next week is noise. But "is anyone else going
          here?" is also worth asking, and it is the same data with the line
          moved, so it is a toggle rather than a second screen.
        */}
        {roundDate && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={countEverything ? 'all' : 'before'}
            onChange={(_, next: string | null) => {
              if (next) setCountEverything(next === 'all');
            }}
            sx={{ mt: 1 }}
          >
            <ToggleButton value="before" sx={{ textTransform: 'none', px: 1.25 }}>
              Before this round
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>
              All saved plans
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {`${totals.covered} covered before + ${totals.fresh} new = ${countLabel(totals.places, 'place', 'places')} in this round`}
          {totals.stops !== totals.places && (
            <Explain
              text={`This round stops ${totals.stops} times at ${totals.places} distinct places — a round trip passes its base twice. Everything on this tab counts places, not stops.`}
            >
              <span style={{ cursor: 'help' }}>{` \u00b7 ${totals.stops} stops`}</span>
            </Explain>
          )}
        </Typography>

        {/* Not coverage, and not buried either: two plans want the same place. */}
        {totals.alsoPlanned > 0 && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
            <Explain
              text={`Places held by saved work planned for ${roundDate ? planned(roundDate) : 'this round\u2019s date'} or after it. Nobody has been there yet, so they count as new ground above; they are a clash to settle rather than ground already covered.`}
            >
              <span style={{ cursor: 'help' }}>
                {`${countLabel(totals.alsoPlanned, 'place', 'places')} also planned for later`}
              </span>
            </Explain>
          </Typography>
        )}
      </Paper>

      {nothingKnown && (
        <Alert severity="info" variant="outlined">
          None of these places is in a saved route or operation for {city.name}. All of
          it is new ground, as far as the saved record goes.
        </Alert>
      )}

      {/* ── 2. stop by stop ──────────────────────────────────────── */}
      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}
        >
          <Typography variant="h3">Stop by stop</Typography>
          <Typography variant="caption" color="text.secondary">
            {listed.length === model.places.length
              ? countLabel(model.places.length, 'place', 'places')
              : `${listed.length} of ${model.places.length}`}
          </Typography>
        </Stack>

        <Stack spacing={1} sx={{ mb: 1 }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a place"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={show}
              onChange={(_, next: PlaceShow | null) => next && setShow(next)}
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>All</ToggleButton>
              <ToggleButton value="covered" sx={{ textTransform: 'none', px: 1.25 }}>Covered</ToggleButton>
              <ToggleButton value="fresh" sx={{ textTransform: 'none', px: 1.25 }}>New</ToggleButton>
              {totals.alsoPlanned > 0 && (
                <ToggleButton value="later" sx={{ textTransform: 'none', px: 1.25 }}>Later</ToggleButton>
              )}
            </ToggleButtonGroup>
            <Select
              size="small"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <MenuItem value="route">In route order</MenuItem>
              <MenuItem value="most">Most covered first</MenuItem>
              <MenuItem value="time">Most planned time</MenuItem>
              <MenuItem value="name">By name</MenuItem>
            </Select>
          </Stack>
        </Stack>

        {listed.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
            {query ? `No place here matches “${query}”.` : 'No place matches that filter.'}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {listed.map((row) => (
              <PlaceRow
                key={row.place.key}
                row={row}
                onOpen={() => setOpenPlace(row.place.key)}
                {...(onShowDetails ? { onShowDetails } : {})}
                {...(onStopFocus ? { onStopFocus } : {})}
                {...(onStopReveal ?? onStopFocus
                  ? { onStopReveal: onStopReveal ?? onStopFocus }
                  : {})}
                {...(onStopHover ? { onStopHover } : {})}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* "across all users" is only true where there are users to be across. */}
      <Typography variant="caption" color="text.secondary">
        Analyzes all saved routes and operations
        {showPeople ? ' across all users,' : ','} not just the plans
        saved in this browser. A place counts as covered if it appears as a stop in a
        saved plan; this does not confirm that the place was actually visited.
      </Typography>

      {/*
        One place, opened from its row: where it is, who has planned it, and
        the rounds it is in. Rendered here rather than inside the row so the
        list holds one window, not forty — and so closing it cannot be
        confused with a row scrolling out of view.
      */}
      <PlaceCoverageDetail
        row={model.places.find((p) => p.place.key === openPlace) ?? null}
        city={city}
        {...(basemapUrl !== undefined ? { basemapUrl } : {})}
        onClose={() => setOpenPlace(null)}
        {...(onStopReveal ?? onStopFocus
          ? { onShowOnMap: onStopReveal ?? onStopFocus }
          : {})}
        {...(onShowDetails ? { onShowDetails } : {})}
      />
    </Stack>
  );
}

/**
 * The stop's own number, as the map and the schedule print it.
 *
 * The same badge in all three places, because a row saying "Georgetown
 * Library" and a pin saying "3" are the same stop, and the reader should not
 * have to count down the list to believe it. Clicking it takes the map
 * there.
 */
function StopBadge({ marker, onClick }: { marker: string; onClick?: () => void }) {
  const colour = marker === 'E' ? STOP_COLOURS.end
    : marker === 'S' || marker === 'S/E' ? STOP_COLOURS.start
      : STOP_COLOURS.visit;

  return (
    <Explain text={onClick ? 'Show this stop on the map' : `Stop ${marker}`}>
      <Box
        onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
        sx={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: colour,
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontSize: marker.length > 2 ? 9 : 12,
          fontWeight: 700,
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        {marker}
      </Box>
    </Explain>
  );
}

/** One place of this round, with its history behind a chevron. */
function PlaceRow({
  row, onOpen, onShowDetails, onStopFocus, onStopReveal, onStopHover,
}: {
  row: PlaceCoverage;
  /** Opens the window that holds this place's whole record. */
  onOpen: () => void;
  onShowDetails?: (poiId: string) => void;
  onStopFocus?: (index: number) => void;
  onStopReveal?: (index: number) => void;
  onStopHover?: (index: number | null) => void;
}) {
  const { place, coverings } = row;
  const covered = row.occurrences > 0;

  /*
   * Common, or in one plan only, or new.
   *
   * Counted in DISTINCT records rather than in occurrences: a place an
   * operation goes back to on three days has been covered three times by one
   * plan, which is not what "several plans have it" means.
   */
  const standing = (() => {
    if (!covered) {
      return row.later.length > 0
        ? {
          label: 'Planned later',
          hint: 'Nobody has covered it. It is in saved work for this round’s own date or after it, so two plans want the same place.',
        }
        : { label: 'New', hint: 'In no saved plan made before this round.' };
    }

    /*
     * The chip says the number, not a word for it.
     *
     * It read "Once" and "Common", which are judgements about a number the
     * reader could not see, and the same word covered two plans and twelve.
     * Counted in DISTINCT plans rather than in occurrences: a place an
     * operation goes back to on three days is in ONE plan, which is not what
     * "several plans have it" would mean.
     */
    return {
      label: `In ${countLabel(row.records, 'saved plan', 'saved plans')}`,
      hint: row.records === 1
        ? `Only ${coverings[0]?.name ?? 'one saved plan'} takes it in. Counted in plans, so a plan that stops here on more than one of its days still counts once.`
        : `${row.records} different saved plans take it in, which may be different people or the same person twice. Counted in plans, so a plan that stops here on more than one of its days still counts once.`,
    };
  })();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderLeft: 3,
        borderLeftColor: covered ? STOP_COLOURS.visit : 'divider',
      }}
      onMouseEnter={() => onStopHover?.(place.index)}
      onMouseLeave={() => onStopHover?.(null)}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', cursor: 'pointer' }}
        /*
          Three things one might want from a row, and three ways to ask.

          The number takes the map there and nothing else, the name opens
          what is known about the place AND takes the map there, and the row
          itself opens this place's own window — where it is, who has
          planned it, and in which rounds. One click used to do all three.
        */
        onClick={onOpen}
      >
        <StopBadge
          marker={place.marker}
          {...(onStopReveal ? { onClick: () => onStopReveal(place.index) } : {})}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                cursor: onShowDetails ?? onStopFocus ? 'pointer' : 'inherit',
                '&:hover': onShowDetails ?? onStopFocus
                  ? { textDecoration: 'underline' } : undefined,
              }}
              /* The name goes to the place, as it does in the schedule. */
              onClick={(e) => {
                if (!onShowDetails && !onStopFocus) return;
                e.stopPropagation();
                /*
                  The panel and the map together: reading about a place and
                  not being shown where it is leaves the reader to find it
                  themselves. The number beside it moves the map WITHOUT
                  opening anything, which is the other half of the pair.
                */
                onStopFocus?.(place.index);
                onShowDetails?.(place.poiId);
              }}
            >
              {place.name}
            </Typography>
            <Explain text={standing.hint}>
              <Chip
                size="small"
                variant="outlined"
                label={standing.label}
                color={covered ? 'default' : 'success'}
                sx={{ height: 18, fontSize: 11, cursor: 'help' }}
              />
            </Explain>
            {place.kind === 'point' && (
              <Explain
                text={`A dropped pin, which has no place to be looked up by. It is matched against saved stops within ${SAME_SPOT_METRES} m of it, and every match below says how near.`}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label="pin"
                  sx={{ height: 18, fontSize: 11, cursor: 'help' }}
                />
              </Explain>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {covered
              ? [
                row.occurrences !== row.records
                  ? `${countLabel(row.occurrences, 'stop', 'stops')} in them`
                  : null,
                row.minutes > 0 ? `${readableHours(row.minutes)} planned` : 'no time set',
                onDay(row.lastAt) ? `last ${onDay(row.lastAt)}` : null,
              ].filter(Boolean).join(' · ')
              : 'Not covered before'}
          </Typography>
          {row.later.length > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
              {`Also in ${countLabel(row.later.length, 'plan', 'plans')} for ${planned(coveringDate(row.later[0]))} or later`}
            </Typography>
          )}
        </Box>

        <Explain text="Open this place on its own: the map, who has planned it, and the saved routes it is in">
          <ChevronRightIcon fontSize="small" color="action" />
        </Explain>
      </Stack>

    </Paper>
  );
}
